/**
 * One "what top performers have in common" card for a single dimension.
 *
 * Props:
 *   title        - human-readable dimension name (e.g. "Activiteitstype")
 *   rows         - server-computed rows: [{ key, topCount, restCount, topPct, restPct, diff }]
 *   labelFor     - fn(key) -> human-readable label for a row
 *   sampleSizes  - { top: N, rest: M } — shown as confidence footer
 *
 * Layout per row:
 *   <label>            +N%
 *   ██████░░ top  45%
 *   █░░░░░░░ rest  8%
 *
 * Bars are horizontal and scaled 0–100.
 */
const BarPair = ({ topPct, restPct }) => (
    <div className='bd-compare-bars'>
        <div className='bd-compare-bar-row'>
            <span className='bd-compare-bar-label'>Top</span>
            <div className='bd-compare-bar'>
                <div
                    className='bd-compare-bar-fill bd-compare-bar-fill--top'
                    style={{ width: `${topPct}%` }}
                />
            </div>
            <span className='bd-compare-bar-value'>{topPct}%</span>
        </div>
        <div className='bd-compare-bar-row'>
            <span className='bd-compare-bar-label'>Rest</span>
            <div className='bd-compare-bar'>
                <div
                    className='bd-compare-bar-fill bd-compare-bar-fill--rest'
                    style={{ width: `${restPct}%` }}
                />
            </div>
            <span className='bd-compare-bar-value'>{restPct}%</span>
        </div>
    </div>
)

const DiffPill = ({ diff }) => {
    if (diff == null || diff === 0) {
        return <span className='bd-diff-pill bd-diff-pill--neutral'>±0%</span>
    }
    const cls = diff > 0 ? 'bd-diff-pill--positive' : 'bd-diff-pill--negative'
    const sign = diff > 0 ? '+' : ''
    return <span className={`bd-diff-pill ${cls}`}>{sign}{diff}%</span>
}

// Show coverage honestly — "Top 1 van 2 · Rest 2 van 3" — so the user knows
// how many companies actually contributed data for this dimension. Essential
// context when many orgs haven't filled in activity/target-group categories.
const CoverageLabel = ({ coverage }) => {
    if (!coverage) return null
    const { top, rest, topTotal, restTotal } = coverage
    return (
        <span className='bd-compare-card-sub'>
            {/* Top {top} van {topTotal} · Rest {rest} van {restTotal} */}
        </span>
    )
}

const BenchmarkComparisonCard = ({ title, rows = [], labelFor = (k) => k, coverage, sampleSizes }) => {
    if (rows.length === 0) return null

    // Warn when the numbers are based on very thin coverage
    const thinCoverage = coverage && (coverage.top + coverage.rest) < 3

    return (
        <div className='bd-compare-card'>
            <div className='bd-compare-card-header'>
                <h3 className='bd-compare-card-title'>{title}</h3>
                {coverage
                    ? <CoverageLabel coverage={coverage} />
                    : sampleSizes && (
                        <span className='bd-compare-card-sub'>
                            Top {sampleSizes.top} vs overige {sampleSizes.rest}
                        </span>
                    )
                }
            </div>

            {thinCoverage && (
                <p className='bd-compare-card-warning'>
                    Nog weinig organisaties hebben dit kenmerk ingevuld — indicatief.
                </p>
            )}

            <div className='bd-compare-rows'>
                {rows.map((row) => (
                    <div key={row.key} className='bd-compare-row'>
                        <div className='bd-compare-row-header'>
                            <span className='bd-compare-row-label'>{labelFor(row.key)}</span>
                            <DiffPill diff={row.diff} />
                        </div>
                        <BarPair topPct={row.topPct} restPct={row.restPct} />
                    </div>
                ))}
            </div>
        </div>
    )
}

export default BenchmarkComparisonCard
